import React, { useState } from 'react';
import { speak, isSpeechSupported } from '../../utils/speak';

const StoryDisplay = ({
  story,
  userAnswers,
  onAnswerChange,
  words
}) => {
  const [revealedBlanks, setRevealedBlanks] = useState({});
  // Find which word corresponds to each sentence with a blank
  const getBlankIndexForSentence = (sentenceIndex) => {
    // Count blanks up to this sentence
    let blankCount = 0;
    for (let i = 0; i <= sentenceIndex; i++) {
      if (story[i].includes('_____')) {
        if (i === sentenceIndex) {
          return blankCount;
        }
        blankCount++;
      }
    }
    return -1;
  };

  const getWordForBlankIndex = (blankIndex) => {
    return words.find(w => w.blankIndex === blankIndex);
  };

  const toggleBlank = (blankIndex) => {
    setRevealedBlanks(prev => ({
      ...prev,
      [blankIndex]: !prev[blankIndex]
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
      {story.map((sentence, sentenceIndex) => {
        const hasBlank = sentence.includes('_____');

        if (!hasBlank) {
          return (
            <p key={sentenceIndex} className="text-lg text-gray-800 leading-relaxed">
              {sentence}
            </p>
          );
        }

        const blankIndex = getBlankIndexForSentence(sentenceIndex);
        const word = getWordForBlankIndex(blankIndex);

        // Show blank with clickable reveal box
        const parts = sentence.split('_____');
        const isRevealed = revealedBlanks[blankIndex];

        return (
          <p key={sentenceIndex} className="text-lg text-gray-800 leading-relaxed">
            {parts[0]}
            <span
              onClick={() => toggleBlank(blankIndex)}
              className={`inline-flex items-center justify-center gap-1.5 min-w-32 max-w-md px-3 py-1 h-8 rounded border-2 cursor-pointer select-none break-words align-middle mx-1 ${
                isRevealed
                  ? 'bg-primary-50 border-primary-400 text-primary-700 font-semibold'
                  : 'bg-gray-100 border-gray-300 text-transparent hover:bg-gray-200 hover:border-gray-400'
              }`}
              style={{ wordBreak: 'break-word' }}
              title={isRevealed ? 'Click to hide' : 'Click to reveal'}
            >
              {isRevealed ? word?.word : (
                <svg width="20px" height="20px" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#292F33" d="M3.651 29.852L29.926 3.576c.391-.391 2.888 2.107 2.497 2.497L6.148 32.349c-.39.391-2.888-2.107-2.497-2.497z"/>
                  <path fill="#66757F" d="M30.442 4.051L4.146 30.347l.883.883L31.325 4.934z"/>
                  <path fill="#E1E8ED" d="M34.546 2.537l-.412-.412l-.671-.671a.967.967 0 0 0-.255-.169a.988.988 0 0 0-1.159.169l-2.102 2.102l.495.495l.883.883l1.119 1.119l2.102-2.102a.999.999 0 0 0 0-1.414zM5.029 31.23l-.883-.883l-.495-.495l-2.209 2.208a.988.988 0 0 0-.169 1.159c.046.09.094.18.169.255l.671.671l.412.412a.999.999 0 0 0 1.414 0l2.208-2.208l-1.118-1.119z"/>
                  <path fill="#F5F8FA" d="M31.325 4.934l2.809-2.809l-.671-.671a.967.967 0 0 0-.255-.169l-2.767 2.767l.884.882zM4.146 30.347L1.273 33.22c.046.09.094.18.169.255l.671.671l2.916-2.916l-.883-.883z"/>
                  <path d="M28.897 14.913l1.542-.571l.6-2.2a.667.667 0 0 1 1.287 0l.6 2.2l1.542.571a.665.665 0 0 1 0 1.25l-1.534.568l-.605 2.415a.667.667 0 0 1-1.293 0l-.605-2.415l-1.534-.568a.665.665 0 0 1 0-1.25M11.961 5.285l2.61-.966l.966-2.61a1.103 1.103 0 0 1 2.07 0l.966 2.61l2.609.966a1.103 1.103 0 0 1 0 2.07l-2.609.966l-.966 2.61a1.105 1.105 0 0 1-2.07 0l-.966-2.61l-2.61-.966a1.104 1.104 0 0 1 0-2.07M24.13 20.772l1.383-.512l.512-1.382a.585.585 0 0 1 1.096 0l.512 1.382l1.382.512a.584.584 0 0 1 0 1.096l-1.382.512l-.512 1.382a.585.585 0 0 1-1.096 0l-.512-1.382l-1.383-.512a.585.585 0 0 1 0-1.096" fill="#FFAC33"/>
                </svg>
              )}
              {isRevealed && word?.word && isSpeechSupported() && (
                <button
                  onClick={(e) => { e.stopPropagation(); speak(word.word); }}
                  className="flex-shrink-0 text-primary-400 hover:text-primary-600 transition-colors"
                  title="Pronounce"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6a7 7 0 010 12M8 9l3-3v12l-3-3H5a1 1 0 01-1-1v-4a1 1 0 011-1h3z" />
                  </svg>
                </button>
              )}
            </span>
            {parts[1]}
          </p>
        );
      })}
    </div>
  );
};

export default StoryDisplay;
